import {
	EmptyState,
	type EmptyStateLabels,
	EmptyStateVariant,
	Pagination,
	Table,
	type TableColumn,
	TableDensity,
	type TableSort,
} from '@today/ui-components'
import { Checkbox, cn } from '@today/ui-primitives'
import type { ReactElement, ReactNode } from 'react'

/** Built-in strings of {@link DataTable}, overridable per locale (tech §59). */
export interface DataTableLabels {
	/** The total-count line. Default `${n} results`. */
	total: (count: number) => string
	/** The bulk-action bar's selected-count line. Default `${n} selected`. */
	selectedCount: (count: number) => string
	/** Accessible name of a row's selection checkbox. Default `Select row`. */
	selectRow: string
	/** Accessible name of the header select-all checkbox. Default `Select all rows`. */
	selectAll: string
}

const DEFAULT_LABELS: DataTableLabels = {
	total: (count) => `${count} results`,
	selectedCount: (count) => `${count} selected`,
	selectRow: 'Select row',
	selectAll: 'Select all rows',
}

/**
 * Props for the {@link DataTable} recipe.
 *
 * @property columns - Column definitions, forwarded to `Table`. The key `SELECTION_COLUMN_KEY`
 *   is reserved for the internal checkbox column; a consumer column must not use it.
 * @property rows - The current page's rows, in render order; the recipe never reorders them.
 * @property getRowId - Pure, stable id for a row; the basis of selection (never the array index).
 * @property sort - The consumer's current sort, forwarded to `Table`.
 * @property onSortChange - Reports the next sort; the consumer maps it to the endpoint's sort param.
 * @property page - Current 1-based page, forwarded to `Pagination`.
 * @property pageCount - Total pages, forwarded to `Pagination` (`<= 1` hides the controls).
 * @property total - Total row count across all pages; rendered in the total line.
 * @property onPageChange - Reports the target page.
 * @property stickyHeader - When true, the table scrolls inside a container with a sticky header.
 * @property density - Row-padding density (`TableDensity`); defaults to `comfortable`.
 * @property selectedRowIds - Controlled selection set; its presence (with `onSelectionChange`) enables selection.
 * @property onSelectionChange - Reports the next selection set; the recipe holds no selection state.
 * @property bulkActions - Content of the bulk-action bar, shown only while a selection exists.
 * @property onClearFilters - Wired to the filtered-empty state's "Clear filters" action.
 * @property emptyStateVariant - Which zero-row `EmptyState` renders; defaults to `filtered-zero`.
 * @property emptyStateLabels - Localised copy for the zero-row `EmptyState`; omitted keys fall back to English.
 * @property labels - Localised built-in strings; each omitted key falls back to English.
 */
export interface DataTableProps<Row> {
	columns: TableColumn<Row>[]
	rows: Row[]
	getRowId: (row: Row) => string
	sort?: TableSort
	onSortChange?: (sort: TableSort) => void
	page: number
	pageCount: number
	total: number
	onPageChange: (page: number) => void
	stickyHeader?: boolean
	density?: TableDensity
	selectedRowIds?: Set<string>
	onSelectionChange?: (selectedRowIds: Set<string>) => void
	bulkActions?: ReactNode
	onClearFilters?: () => void
	labels?: Partial<DataTableLabels>
	emptyStateVariant?: EmptyStateVariant
	emptyStateLabels?: Partial<EmptyStateLabels>
	'aria-label'?: string
}

/**
 * React key of the internal selection column. The sentinel is namespaced so it cannot collide
 * with a consumer-supplied column key (`Table` keys its header/cells by `column.key`), so
 * enabling selection never produces a duplicate-key render.
 */
export const SELECTION_COLUMN_KEY = '@today/data-table/selection'

/** Set with `id` toggled in (if absent) or out (if present), without mutating the input. */
function toggled(ids: Set<string>, id: string): Set<string> {
	const next = new Set(ids)
	if (next.has(id)) next.delete(id)
	else next.add(id)
	return next
}

/** The selection column prepended when selection is enabled: per-row + header select-all checkboxes. */
function selectionColumn<Row>(
	rows: Row[],
	getRowId: (row: Row) => string,
	selectedRowIds: Set<string>,
	onSelectionChange: (next: Set<string>) => void,
	labels: DataTableLabels,
): TableColumn<Row> {
	const visibleIds = rows.map(getRowId)
	const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedRowIds.has(id))
	const someVisibleSelected = visibleIds.some((id) => selectedRowIds.has(id))

	function toggleAll(): void {
		const next = new Set(selectedRowIds)
		// Page-scoped: add/remove only the visible ids; off-page selections are preserved.
		if (allVisibleSelected) for (const id of visibleIds) next.delete(id)
		else for (const id of visibleIds) next.add(id)
		onSelectionChange(next)
	}

	return {
		key: SELECTION_COLUMN_KEY,
		header: (
			<Checkbox
				aria-label={labels.selectAll}
				isSelected={allVisibleSelected}
				isIndeterminate={!allVisibleSelected && someVisibleSelected}
				onChange={toggleAll}
			/>
		),
		cell: (row) => (
			<Checkbox
				aria-label={labels.selectRow}
				isSelected={selectedRowIds.has(getRowId(row))}
				onChange={() => onSelectionChange(toggled(selectedRowIds, getRowId(row)))}
			/>
		),
	}
}

/** The scrollable table body: the `Table` molecule wrapped in the optional sticky-scroll container. */
function GridBody<Row>({
	ariaLabel,
	columns,
	rows,
	sort,
	onSortChange,
	density,
	stickyHeader,
	onClearFilters,
	emptyStateVariant,
	emptyStateLabels,
}: {
	ariaLabel?: string
	columns: TableColumn<Row>[]
	rows: Row[]
	sort?: TableSort
	onSortChange?: (sort: TableSort) => void
	density: TableDensity
	stickyHeader: boolean
	onClearFilters?: () => void
	emptyStateVariant: EmptyStateVariant
	emptyStateLabels?: Partial<EmptyStateLabels>
}): ReactElement {
	return (
		<div
			// The consumer bounds the height (e.g. a flex parent); we only own the scroll + sticky.
			className={cn(stickyHeader && 'overflow-auto [&_thead]:sticky [&_thead]:top-0 [&_thead]:bg-ds-surface')}
		>
			<Table
				aria-label={ariaLabel}
				columns={columns}
				rows={rows}
				sort={sort}
				onSortChange={onSortChange}
				density={density}
				emptyState={
					<EmptyState variant={emptyStateVariant} labels={emptyStateLabels} onClearFilters={onClearFilters} />
				}
			/>
		</div>
	)
}

/** The total-count line and the pagination controls below the grid. */
function GridFooter({
	total,
	totalLabel,
	page,
	pageCount,
	onPageChange,
}: {
	total: number
	totalLabel: (count: number) => string
	page: number
	pageCount: number
	onPageChange: (page: number) => void
}): ReactElement {
	return (
		<div className="flex items-center justify-between gap-ds-3">
			<span data-testid="data-table-total" className="text-ds-sm text-ds-text-muted">
				{totalLabel(total)}
			</span>
			<Pagination page={page} pageCount={pageCount} onPageChange={onPageChange} />
		</div>
	)
}

/** The bulk-action bar — a selected-count line plus the consumer's actions, shown only with a selection. */
function BulkActionBar({ label, actions }: { label: string; actions: ReactNode }): ReactElement {
	return (
		<div
			data-testid="bulk-action-bar"
			className="flex items-center gap-ds-3 rounded-ds-md border border-ds-border bg-ds-surface-muted px-3 py-2"
		>
			<span className="text-ds-sm text-ds-text">{label}</span>
			<div className="flex flex-wrap gap-ds-2">{actions}</div>
		</div>
	)
}

/**
 * Server-driven data grid composing `Table` + `Pagination` + `EmptyState`.
 *
 * @remarks
 * - **Composition, not re-implementation:** sort and paging are forwarded to the molecules
 *   unchanged; the recipe only adds the selection column, the total line, the bulk-action bar,
 *   the density/sticky wrapper and the filtered-empty wiring.
 * - **Controlled:** sort, page and selection live in the consumer's hook. The recipe computes
 *   the next value and reports it; it holds no state of its own.
 * - **Selection:** enabled by supplying both `selectedRowIds` and `onSelectionChange`. Row
 *   identity is `getRowId` (never the array index). Select-all is page-scoped and tri-state.
 * - **Empty:** zero rows render the `EmptyState` in the table body (`filtered-zero` unless
 *   `emptyStateVariant` says otherwise, copy from `emptyStateLabels`); the header stays. Lists return `200 {data:[],total:0}`, so this is a state, not an absent table.
 * - **Styling:** every colour, radius and spacing class is a `ds-*` token utility.
 *
 * @see packages/ui-recipes/src/data-table/data-table.stories.tsx for visual examples
 */
export function DataTable<Row>({
	columns,
	rows,
	getRowId,
	sort,
	onSortChange,
	page,
	pageCount,
	total,
	onPageChange,
	stickyHeader = false,
	density = TableDensity.Comfortable,
	selectedRowIds,
	onSelectionChange,
	bulkActions,
	onClearFilters,
	labels,
	emptyStateVariant = EmptyStateVariant.FILTERED_ZERO,
	emptyStateLabels,
	'aria-label': ariaLabel,
}: DataTableProps<Row>): ReactElement {
	const text = { ...DEFAULT_LABELS, ...labels }
	const selectionEnabled = selectedRowIds !== undefined && onSelectionChange !== undefined

	const tableColumns = selectionEnabled
		? [selectionColumn(rows, getRowId, selectedRowIds, onSelectionChange, text), ...columns]
		: columns

	const selectedCount = selectedRowIds?.size ?? 0
	const showBulkBar = selectionEnabled && bulkActions !== undefined && selectedCount > 0

	return (
		<div data-density={density} className="flex flex-col gap-ds-3">
			{showBulkBar ? <BulkActionBar label={text.selectedCount(selectedCount)} actions={bulkActions} /> : null}
			<GridBody
				ariaLabel={ariaLabel}
				columns={tableColumns}
				rows={rows}
				sort={sort}
				onSortChange={onSortChange}
				density={density}
				stickyHeader={stickyHeader}
				onClearFilters={onClearFilters}
				emptyStateVariant={emptyStateVariant}
				emptyStateLabels={emptyStateLabels}
			/>
			<GridFooter total={total} totalLabel={text.total} page={page} pageCount={pageCount} onPageChange={onPageChange} />
		</div>
	)
}
